import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'

/** Host custom property `.fab` adds to its resting `bottom` (see the stylesheet). */
const OFFSET_CSS_PROPERTY = '--fab-offset'
/** Pointer travel (px) before a press becomes a drag; below it the gesture is a click. */
const DRAG_THRESHOLD_PX = 4
/** Fingers wobble: a tap must survive more travel than a mouse press. */
const DRAG_THRESHOLD_TOUCH_PX = 10
/** Arrow-key nudge (px) for keyboard users. */
const KEYBOARD_STEP_PX = 16

/** One press-and-move on the button, from `pointerdown` to release. */
interface DragGesture {
  pointerId: number
  /** Travel that turns the press into a move — depends on the pointer type. */
  thresholdPx: number
  /** Pointer Y the travel is measured from; re-based when the threshold is crossed. */
  originClientY: number
  /** Offset painted when the press started. */
  originOffsetPx: number
  /** Ceiling for this gesture, measured once at the press. */
  maxOffsetPx: number
  /**
   * Crossed the threshold: this is a move, and the `click` it ends with must
   * not count as an activation.
   */
  moved: boolean
}

/**
 * Floating action button pinned to the right edge that the person can drag up
 * and down — from its resting height to the middle of the viewport — so it can
 * be pushed off whatever it happens to cover. Icon and badge are projected
 * content; the skin (background, radius, size, z-index) is handed down through
 * `--fab-*` custom properties set on the host by the consumer's stylesheet.
 *
 * One axis, one writer: the component paints `--fab-offset` on its host and the
 * button adds it to its resting `bottom`. The height is remembered under
 * `storageKey` per device — what the button covers depends on the screen, not
 * on who is logged in.
 *
 * ```html
 * <draggable-fab class="my-fab" storageKey="trendmulti.my-fab.offsetPx"
 *   [ariaLabel]="'my.label' | translate" (activated)="open()">
 *   <i class="ri-chat-3-line font-24" aria-hidden="true"></i>
 * </draggable-fab>
 * ```
 */
@Component({
    selector: 'draggable-fab',
    templateUrl: './draggable-fab.component.html',
    styleUrls: ['./draggable-fab.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DraggableFabComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Accessible name of the button — it renders an icon, so this is its only label. */
  @Input({ required: true }) ariaLabel!: string

  /**
   * localStorage key the chosen height is remembered under. Without it the
   * position lasts only as long as this instance — a host that mounts the FAB
   * conditionally (hiding it while a window is open, say) should pass one.
   */
  @Input() storageKey: string | null = null

  /**
   * A press that did not turn into a drag, or a keyboard activation — the
   * button's "click". A drag never emits.
   */
  @Output() readonly activated = new EventEmitter<void>()

  @ViewChild('fab', { static: true })
  private readonly button!: ElementRef<HTMLButtonElement>

  /**
   * The height the person dragged the button to, above its resting `bottom`.
   * This is what gets persisted; what is PAINTED is this capped to the current
   * viewport (`applyOffset`), so a temporarily smaller window never overwrites
   * the chosen height.
   */
  private offsetPx = 0

  /** The offset currently painted — a drag starts from here, not from the remembered wish. */
  private appliedOffsetPx = 0

  private drag: DragGesture | null = null

  /** Tears down the out-of-zone `pointermove` listener of the running gesture. */
  private stopMoveListening: (() => void) | null = null

  /** Set by a drag that ended on the button: the `click` that follows meant "move", not "activate". */
  private suppressNextClick = false

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.offsetPx = this.restoreOffset()
  }

  /**
   * First paint, clamped to THIS viewport: a height saved on a taller screen
   * must not park the button above the middle here. Runs before the browser
   * paints, so the button never flashes at its resting height first.
   */
  ngAfterViewInit(): void {
    this.applyOffset()
  }

  ngOnDestroy(): void {
    // A drag cut short by the host tearing the button down (a window replacing
    // it, say) still keeps its result; the hand-registered listener goes too.
    this.endDrag()
  }

  // --- handlers ----------------------------------------------------------------

  /**
   * A press arms a drag; whether it becomes one is decided by travel
   * (`onPointerMove`), so a plain click keeps activating through `onClick`. The
   * pointer is captured so the button keeps receiving the gesture wherever it
   * wanders, and the move handler is registered OUTSIDE the zone: a full
   * change-detection pass per `pointermove` over a busy page is exactly the
   * jank a drag must not have.
   */
  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || !event.isPrimary) return
    const fab = this.button.nativeElement
    // A gesture the browser never released (pointer lost) must not block this one.
    this.endDrag()
    this.suppressNextClick = false
    this.drag = {
      pointerId: event.pointerId,
      thresholdPx:
        event.pointerType === 'touch'
          ? DRAG_THRESHOLD_TOUCH_PX
          : DRAG_THRESHOLD_PX,
      originClientY: event.clientY,
      originOffsetPx: this.appliedOffsetPx,
      maxOffsetPx: this.measureMaxOffset(),
      moved: false,
    }
    try {
      fab.setPointerCapture(event.pointerId)
    } catch {
      // Inactive pointer id — the gesture still works while the pointer stays on the button.
    }
    this.stopMoveListening = this.ngZone.runOutsideAngular(() =>
      this.renderer.listen(fab, 'pointermove', (move: PointerEvent) =>
        this.onPointerMove(move),
      ),
    )
  }

  /**
   * `pointerup`, `pointercancel` and `lostpointercapture` all land here; the
   * first to arrive does the work, the others find nothing to end.
   */
  onPointerRelease(): void {
    this.endDrag()
  }

  /**
   * A drag that ends on the button still yields a `click` (capture routes it
   * here) — that gesture meant "move", so it is swallowed once. The flag is
   * reset by the next press, so a drag that ended without a click (cancelled
   * pointer) never eats a later, real one.
   */
  onClick(): void {
    if (this.suppressNextClick) {
      this.suppressNextClick = false
      return
    }
    this.activated.emit()
  }

  /** Arrow keys move the focused button one step — the keyboard's route to the travel a drag gives. */
  onArrowKey(event: Event, direction: 'up' | 'down'): void {
    // The page must not scroll under a button that is being repositioned.
    event.preventDefault()
    const step = direction === 'up' ? KEYBOARD_STEP_PX : -KEYBOARD_STEP_PX
    const next = this.clampOffset(
      this.appliedOffsetPx + step,
      this.measureMaxOffset(),
    )
    this.offsetPx = next
    this.paintOffset(next)
    this.persistOffset()
  }

  /** A smaller window caps the travel; a bigger one gives the remembered height back. */
  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.drag) this.applyOffset()
  }

  // --- mechanics ---------------------------------------------------------------

  private onPointerMove(event: PointerEvent): void {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.pointerId) return
    // Up is positive: the offset grows as the pointer climbs.
    let travel = drag.originClientY - event.clientY
    if (!drag.moved) {
      if (Math.abs(travel) < drag.thresholdPx) return
      // Crossing the threshold starts the move from where the button rests —
      // re-based, so it does not jump by the threshold on the first frame.
      drag.moved = true
      drag.originClientY = event.clientY
      travel = 0
      this.renderer.addClass(this.button.nativeElement, 'fab--dragging')
    }
    this.paintOffset(
      this.clampOffset(drag.originOffsetPx + travel, drag.maxOffsetPx),
    )
  }

  private endDrag(): void {
    const drag = this.drag
    if (!drag) return
    this.drag = null
    this.stopMoveListening?.()
    this.stopMoveListening = null
    this.renderer.removeClass(this.button.nativeElement, 'fab--dragging')
    if (drag.moved) {
      this.suppressNextClick = true
      this.offsetPx = this.appliedOffsetPx
      this.persistOffset()
    }
  }

  /** Paints the remembered offset, capped to what the current viewport allows. */
  private applyOffset(): void {
    this.paintOffset(this.clampOffset(this.offsetPx, this.measureMaxOffset()))
  }

  /** The single writer of the host custom property the button reads its `bottom` from. */
  private paintOffset(px: number): void {
    this.appliedOffsetPx = px
    this.renderer.setStyle(
      this.host.nativeElement,
      OFFSET_CSS_PROPERTY,
      `${px}px`,
      RendererStyleFlags2.DashCase,
    )
  }

  private clampOffset(px: number, maxPx: number): number {
    return Math.min(maxPx, Math.max(0, px))
  }

  /**
   * How far up this viewport lets the button go: its centre stops at the middle
   * of the screen. Read off the element (resting `bottom`, height) so the
   * stylesheet stays the only place those lengths are written; the computed
   * `bottom` already includes the painted offset, hence the subtraction.
   * Transforms (the hover lift) touch neither value. An element that cannot be
   * measured (not in the document) gets no ceiling rather than a zero one.
   */
  private measureMaxOffset(): number {
    const style = getComputedStyle(this.button.nativeElement)
    const restingBottom = parseFloat(style.bottom) - this.appliedOffsetPx
    const height = parseFloat(style.height)
    const max = window.innerHeight / 2 - restingBottom - height / 2
    return Number.isFinite(max) ? Math.max(0, max) : Number.POSITIVE_INFINITY
  }

  private persistOffset(): void {
    if (!this.storageKey) return
    try {
      localStorage.setItem(this.storageKey, String(Math.round(this.offsetPx)))
    } catch {
      // Storage unavailable (private mode) — the position lasts the instance.
    }
  }

  private restoreOffset(): number {
    if (!this.storageKey) return 0
    try {
      const stored = Number(localStorage.getItem(this.storageKey))
      return Number.isFinite(stored) && stored > 0 ? stored : 0
    } catch {
      return 0
    }
  }
}
